import { useRouter } from 'next/compat/router';
import styled, { createGlobalStyle } from 'styled-components';

import ContainerSmall from '@/components/ContainerSmall/ContainerSmall';
import LocalizedLink from '@/components/LocalizedLink/LocalizedLink';
import Navigation from '@/components/Navigation/Navigation';
import Seo from '@/components/Seo/Seo';
import { LANGUAGES } from '@/lib/constants';
import BackButton from '@/modules/404/BackButton';
import Message from '@/modules/404/Message';

const WebsiteHeight = createGlobalStyle`
  html, body {
    min-height: 100vh;
  }
`;

const PAGES = [
	{
		title: 'Home',
		link: '/',
	},
	{
		title: 'Blog',
		link: '/blog',
	},
	{
		title: 'Success Stories',
		link: '/success-stories',
	},
	{
		title: 'Viber for Business',
		link: '/viber-for-business',
	},
];

const MESSAGES = [
	{
		author: {
			name: '404 bot',
			icon: '/images/icons/cat.svg',
		},
		text: [
			{ content: 'Hi there!', time: '18:36' },
			{
				content: 'I am here to help! Here are the pages you can visit',
				time: '18:37',
			},
		],
	},
	{
		author: {
			icon: '/images/icons/cat.svg',
		},
		text: [
			{
				content: 'Or click on the button below to return to the previous page',
				time: '18:37',
			},
		],
	},
];

const NotFoundPage = () => {
	const router = useRouter();
	const linkLang = router?.asPath?.split('/')?.[1];
	const lang = LANGUAGES.some(({ code }) => linkLang === code)
		? linkLang
		: 'en';

	return (
		<>
			<Seo url="/404" title="Not Found Page" description="Error 404" />

			<Page>
				<Navigation lang={lang as string} />

				<Left
					loading="lazy"
					alt="Gradient"
					src="/images/not-found-page/left-gradient.png"
				/>

				<Right
					loading="lazy"
					alt="Gradient"
					src="/images/not-found-page/right-gradient.png"
				/>

				<Bottom
					loading="lazy"
					alt="Gradient"
					src="/images/not-found-page/bottom-gradient.png"
				/>

				<WebsiteHeight />

				<Wrapper>
					<ContainerSmall>
						<PageContent>
							<StatusCode>404</StatusCode>

							<Title>This page doesn&apos;t exist</Title>

							<Subtitle>But you look great today :)</Subtitle>

							<Chat>
								{MESSAGES.slice(0, MESSAGES.length - 1)?.map(
									(thread, index) => (
										<Thread key={`page_404_thread_1_${index}`}>
											<Icon>
												<img
													alt={thread.author?.name}
													src={thread.author?.icon}
												/>
											</Icon>

											<Messages>
												{thread.text?.map((message, index) => (
													<Message
														key={`page_404_thread_text_1_${index}`}
														index={index}
														thread={thread}
														message={message}
													/>
												))}
											</Messages>
										</Thread>
									),
								)}
							</Chat>

							<Suggestions>
								{PAGES.map((page, index) => (
									<Suggestion key={`page_404_suggestion_${index}`}>
										<LocalizedLink href={page.link}>{page.title}</LocalizedLink>
									</Suggestion>
								))}
							</Suggestions>

							<Chat>
								{MESSAGES.slice(MESSAGES.length - 1, MESSAGES.length)?.map(
									(thread, index) => (
										<Thread key={`page_404_thread_2_${index}`}>
											<Icon>
												<img
													alt={thread.author?.name}
													src={thread.author?.icon}
												/>
											</Icon>

											<Messages>
												{thread.text?.map((message, index) => (
													<Message
														key={`page_404_thread_text_2_${index}`}
														index={index}
														thread={thread}
														message={message}
													/>
												))}
											</Messages>
										</Thread>
									),
								)}
							</Chat>

							<BackButton text="Go back" />
						</PageContent>
					</ContainerSmall>
				</Wrapper>
			</Page>
		</>
	);
};

const Page = styled.div`
	display: flex;
	position: relative;
	overflow: hidden;
	flex-direction: column;
	min-height: 100vh;

	@media (min-width: 900px) {
		height: 100%;
	}
`;

const PageContent = styled.div`
	position: relative;
	z-index: 10;
`;

const Thread = styled.div`
	position: relative;
	margin-top: 25px;
	margin-bottom: 10px;
`;

const Icon = styled.div`
	width: 80px;
	height: 80px;
	background-color: var(--color-purple);
	border-radius: 30px;
	display: flex;
	align-items: center;
	position: absolute;
	left: -100px;

	img {
		display: block;
		margin: auto;
		max-width: 90%;
	}

	@media (max-width: 780px) {
		width: 60px;
		height: 60px;
		border-radius: 24px;
		left: -75px;

		img {
			max-width: 70%;
		}
	}

	@media (max-width: 450px) {
		width: 47px;
		height: 47px;
		border-radius: 18px;
		left: -60px;
	}
`;

const Messages = styled.div`
	.message-404:first-child {
		.message-404__content {
			padding-bottom: 22px;
		}
	}
	.message-404:last-child {
		.message-404__content {
			padding-bottom: 32px;
		}
	}
`;

const StatusCode = styled.div`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple-50);
	margin-bottom: 10px;

	@media (max-width: 600px) {
		font-size: 24px;
		line-height: 32px;
	}
`;

const Subtitle = styled.h3`
	font-family: var(--font-sans);

	font-size: 28px;
	line-height: 25px;
	text-align: center;
	color: var(--color-purple-50);
	margin-top: 10px;
	margin-bottom: 40px;

	@media (max-width: 600px) {
		font-size: 22px;
		line-height: 32px;
		margin-top: 10px;
		margin-bottom: 20px;
	}
`;

const Chat = styled.div`
	margin: 0 auto;
	width: 540px;

	@media (max-width: 780px) {
		width: 400px;
	}

	@media (max-width: 600px) {
		width: 100%;
	}

	@media (max-width: 530px) {
		padding-left: 50px;
	}

	@media (max-width: 510px) {
		padding-right: 50px;
	}
`;

const Suggestion = styled.div`
	a {
		display: block;
		color: var(--color-white);
		text-decoration: none;
		min-width: 110px;
		padding: 10px 11px;
		background-color: var(--color-purple-50);
		transition: 0.3s;
		font-family: var(--font-sans);

		font-size: 17px;
		line-height: 25px;
		text-align: center;
		color: var(--color-white);
		margin-left: 1px;
		margin-right: 1px;
		padding-right: 26px;
		padding-left: 26px;

		@media (max-width: 530px) {
			font-size: 14px;
			line-height: 28px;
			padding: 7px 11px;
		}

		&:hover {
			background-color: var(--color-purple-90);
		}
	}

	@media (max-width: 600px) {
		margin-bottom: 1px;
		margin-top: 1px;
	}
`;

const Suggestions = styled.div`
	display: flex;
	justify-content: center;
	position: relative;
	overflow: hidden;

	@media (max-width: 530px) {
		padding-left: 50px;
	}

	${Suggestion}:first-child a {
		border-top-left-radius: 16px;
		border-bottom-left-radius: 16px;
	}

	${Suggestion}:last-child a {
		border-top-right-radius: 16px;
		border-bottom-right-radius: 16px;
	}

	@media (max-width: 600px) {
		display: box;
		width: auto;
		overflow-x: scroll;
		overflow-y: hidden;

		${Suggestion}:nth-child(1) a {
			border-top-left-radius: 16px;
			border-bottom-left-radius: 16px;
		}

		${Suggestion}:nth-child(2) a {
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}

		${Suggestion}:nth-child(3) a {
			border-top-left-radius: 0px;
		}

		${Suggestion}:nth-child(4) a {
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
			border-top-right-radius: 16px;
			border-bottom-right-radius: 16px;
		}
	}
`;

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 81px;
	padding-top: 40px;
	padding-bottom: 120px;
	position: relative;
	overflow: hidden;

	@media (max-width: 600px) {
		margin-top: 65px;
	}
`;

const Left = styled.img`
	position: absolute;
	left: 0;
	top: 0;
	z-index: 9;

	@media (max-width: 800px) {
		opacity: 0.7;
	}

	@media (max-width: 600px) {
		opacity: 0.4;
	}
`;

const Right = styled.img`
	position: absolute;
	right: 0;
	bottom: 0;
	z-index: 9;

	@media (max-width: 800px) {
		opacity: 0.7;
	}

	@media (max-width: 600px) {
		opacity: 0.4;
	}
`;

const Bottom = styled.img`
	position: absolute;
	left: 0;
	bottom: 0;
	z-index: 9;

	@media (max-width: 800px) {
		opacity: 0.7;
	}

	@media (max-width: 600px) {
		opacity: 0.4;
	}
`;

const Title = styled.h1`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	text-align: center;
	color: var(--color-purple);

	@media (max-width: 600px) {
		font-size: 30px;
		line-height: 40px;
	}
`;

export default NotFoundPage;
